/**
 * One place to turn a failed mutation into a string a host can read.
 *
 * Studio's mutations failed silently in six places: the spinner stopped, nothing
 * appeared, and the 400 was only visible in the network tab — so "enter a
 * latitude without a longitude" looked exactly like "the save did nothing". The
 * tabs that did surface errors each re-derived the message inline, in slightly
 * different ways, which is why the other six were easy to forget. Extract once,
 * use everywhere.
 */

/** Shapes the OrchestratorAPI and the concierge-builder API use for failures. */
interface ApiErrorBody {
  error?: unknown;
  detail?: unknown;
  title?: unknown;
  message?: unknown;
}

/**
 * Best-effort message for any mutation error, '' when there is nothing to show.
 * Falls back to the transport-level message so a network failure or a CORS
 * rejection still says something rather than nothing.
 */
export function mutationErrorMessage(error: unknown): string {
  if (!error) return '';

  const body = (error as { response?: { data?: ApiErrorBody | string } })?.response?.data;
  if (typeof body === 'string' && body.trim()) return body.trim();

  if (body && typeof body === 'object') {
    // `title` is what ASP.NET's ProblemDetails puts the summary in; the rest are
    // this API's own hand-rolled error bodies.
    for (const value of [body.error, body.detail, body.title, body.message]) {
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }

  const message = (error as Error)?.message;
  return typeof message === 'string' ? message.trim() : '';
}
